import { z } from 'zod';

import type { HandoffDefaultSource } from './marketplace-order-handoff-source.js';

export const MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX = 2000;
export const MARKETPLACE_ORDER_HANDOFF_OVERRIDE_NOTE_MAX = 4000;

export const MarketplaceOrderHandoffDefaultsSchema = z.object({
  brandGuidelines: z.string().max(MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX).default(''),
  techSpecs: z.string().max(MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX).default(''),
  policyRestrictions: z.string().max(MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX).default(''),
  dosAndDonts: z.string().max(MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX).default(''),
  productAccuracy: z.string().max(MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX).default(''),
  thumbnailGuidelines: z.string().max(MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX).default(''),
  fontsLicenceProof: z.string().max(MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX).default(''),
  coloursHex: z.string().max(MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX).default(''),
  logos: z.string().max(MARKETPLACE_ORDER_HANDOFF_DEFAULT_VALUE_MAX).default(''),
});
export type MarketplaceOrderHandoffDefaults = z.infer<typeof MarketplaceOrderHandoffDefaultsSchema>;

export const EMPTY_MARKETPLACE_ORDER_HANDOFF_DEFAULTS: MarketplaceOrderHandoffDefaults =
  MarketplaceOrderHandoffDefaultsSchema.parse({});

/** Shown when an org has not written its own thumbnail guidelines. */
export const DEFAULT_THUMBNAIL_GUIDELINES =
  'The product and brand must stay readable. Do not add misleading text.';

export const THUMBNAIL_REQUEST_EXPECTATION = 'Optional but expected.';

export function resolveThumbnailGuidelines(value: string | null | undefined): string {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : DEFAULT_THUMBNAIL_GUIDELINES;
}

/**
 * Guideline text recorded on an order snapshot.
 * Undefined when the snapshot never stored the field — including legacy orders
 * and imports — so renderers do not invent the request from current org defaults.
 * A blank string is recorded and still means "use the Adge default".
 */
export function recordedThumbnailGuidelines(snapshot: unknown): string | undefined {
  if (snapshot == null || typeof snapshot !== 'object' || Array.isArray(snapshot)) return undefined;
  if (!Object.hasOwn(snapshot, 'thumbnailGuidelines')) return undefined;
  const raw = (snapshot as { thumbnailGuidelines?: unknown }).thumbnailGuidelines;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && 'value' in raw) {
    const value = (raw as { value?: unknown }).value;
    if (typeof value === 'string') return value;
  }
  return undefined;
}

/** One line per selected ratio, or a single default slot when the order has none. */
export function thumbnailRequestLines(ratios: readonly string[] | null | undefined): string[] {
  const selected = ratios ?? [];
  if (selected.length === 0) return ['One default thumbnail'];
  return selected.map((ratio) => `One thumbnail at ${ratio}`);
}

export function formatThumbnailRequestPlainText(input: {
  guidelines: string | null | undefined;
  ratios?: readonly string[] | null;
}): string {
  return [
    'Thumbnails',
    THUMBNAIL_REQUEST_EXPECTATION,
    ...thumbnailRequestLines(input.ratios),
    resolveThumbnailGuidelines(input.guidelines),
  ].join('\n');
}

export const MarketplaceOrderHandoffOverrideNoteSchema = z
  .string()
  .trim()
  .max(MARKETPLACE_ORDER_HANDOFF_OVERRIDE_NOTE_MAX);
export type MarketplaceOrderHandoffOverrideNote = z.infer<
  typeof MarketplaceOrderHandoffOverrideNoteSchema
>;

export interface MarketplaceOrderHandoffDefaultItemMeta {
  key: keyof MarketplaceOrderHandoffDefaults;
  section: 'rules' | 'brandKit';
  label: string;
  source: HandoffDefaultSource;
  description: string;
}

export const MARKETPLACE_ORDER_HANDOFF_DEFAULT_ITEMS: MarketplaceOrderHandoffDefaultItemMeta[] = [
  {
    key: 'brandGuidelines',
    section: 'rules',
    label: 'Brand guidelines',
    source: 'Folder',
    description:
      "The client's full brand or video guidelines. The authority on anything the order does not state explicitly.",
  },
  {
    key: 'techSpecs',
    section: 'rules',
    label: 'Tech specs',
    source: 'Folder',
    description: 'Frame rate, codec, export preset and audio levels. Overrides any spec assumed elsewhere in the order.',
  },
  {
    key: 'policyRestrictions',
    section: 'rules',
    label: 'Policy & restrictions',
    source: 'Folder',
    description:
      'What gets an ad rejected — platform policy, wardrobe and framing limits, prohibited shots. Read before the first shot, not after a takedown.',
  },
  {
    key: 'dosAndDonts',
    section: 'rules',
    label: "Do's & Don'ts",
    source: 'Folder',
    description:
      'What must appear and what must never appear — write it here, or paste a link to where the partner can find it.',
  },
  {
    key: 'productAccuracy',
    section: 'rules',
    label: 'Product accuracy',
    source: 'Package',
    description:
      'The positive build spec for how the product must look and behave on screen. For AI work this is the highest-risk component, because the product is generated rather than filmed.',
  },
  {
    key: 'thumbnailGuidelines',
    section: 'rules',
    label: 'Thumbnail guidelines',
    source: 'Folder',
    description:
      'How the partner should make each thumbnail. Leave blank to use the Adge default. Optional, and expected with every video.',
  },
  {
    key: 'fontsLicenceProof',
    section: 'brandKit',
    label: 'Fonts + licence proof',
    source: 'Folder',
    description:
      'Font files for every weight the brand uses, with evidence they are licensed for this use. The licence matters as much as the file when type is embedded in video.',
  },
  {
    key: 'coloursHex',
    section: 'brandKit',
    label: 'Colours / hex',
    source: 'Folder',
    description: 'Exact primary and secondary values. Removes the guesswork of matching a colour off a screenshot.',
  },
  {
    key: 'logos',
    section: 'brandKit',
    label: 'Logos',
    source: 'Folder',
    description: 'Light and dark versions with clear-space rules. Covers both the file and the rules for placing it.',
  },
];

export function parseMarketplaceOrderHandoffDefaults(raw: unknown): MarketplaceOrderHandoffDefaults {
  const parsed = MarketplaceOrderHandoffDefaultsSchema.safeParse(raw ?? {});
  return parsed.success ? parsed.data : EMPTY_MARKETPLACE_ORDER_HANDOFF_DEFAULTS;
}
